import * as _4 from 'lodash4';

/**
 * Class responsible for observing and removing `tabindex="0"` attributes
 * from a target element and its children, including elements in Shadow DOMs.
 */
export class TabIndexCleaner {
  private observer: MutationObserver;

  // Debounced handler to reduce redundant executions
  private handleMutations = _4.debounce(() => this.removeTabIndexFromChildren(this.target), 500);

  /**
   * Initializes the cleaner for a specific target element.
   * @param target - The root element to observe for tabindex changes.
   */
  constructor(private target: HTMLElement) {
    this.observer = new MutationObserver((mutations) => {
      // Check if any mutation involves a 'tabindex' attribute change
      const hasTabindexMutations = mutations.some((mutation) => mutation.attributeName === 'tabindex');
      if (hasTabindexMutations) {
        this.handleMutations(); // Trigger debounced removal
      }
    });
  }

  /**
   * Removes the `tabindex="0"` attribute from an individual element.
   * @param element - The element to clean tabindex from.
   */
  private removeTabIndexFromElement(element: HTMLElement) {
    if (
      element instanceof HTMLElement &&
      element.hasAttribute('tabindex') &&
      element.getAttribute('tabindex') === '0'
    ) {
      element.removeAttribute('tabindex'); // Remove only if tabindex="0"
    }
  }

  /**
   * Recursively removes `tabindex="0"` from the target element and its descendants.
   * It also traverses the Shadow DOM if present.
   * @param element - The root element or Shadow DOM to clean.
   */
  private removeTabIndexFromChildren(element: HTMLElement | ShadowRoot) {
    // Clean the root element
    this.removeTabIndexFromElement(element as HTMLElement);

    // Query all child elements with `tabindex="0"`
    const children = element.querySelectorAll('[tabindex="0"]');
    children.forEach((child) => child.removeAttribute('tabindex'));

    // Traverse through Shadow DOM if present
    element.querySelectorAll('*').forEach((child) => {
      const shadowRoot = (child as HTMLElement).shadowRoot;

      if (shadowRoot) {
        this.removeTabIndexFromChildren(shadowRoot); // Recursively clean shadow roots
      }
    });
  }

  /**
   * Starts observing the target element for any changes in its child nodes or attributes,
   * particularly focusing on `tabindex` attribute changes.
   */
  startObserving() {
    this.observer.observe(this.target, {
      childList: true, // Observe additions/removals of child elements
      subtree: true, // Include all descendants
      attributes: true, // Observe attribute changes
      attributeFilter: ['tabindex'] // Only watch for changes to the tabindex attribute
    });

    // Perform an initial cleanup
    this.handleMutations();
    return this;
  }

  /**
   * Stops observing the target element for changes.
   */
  stopObserving() {
    this.observer.disconnect();
    return this;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/utilities/tabindex/tab_index_cleaner.ts